import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Grid, TextField, Button, Checkbox, FormControlLabel, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import WarningIcon from '@material-ui/icons/Warning';
import axios from 'axios';
import { API_URL } from '../../config';
import { connect } from 'react-redux';
import { currentRouteSet } from '../../actions/actions';
import swal from 'sweetalert';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    color: '#333',
    fontWeight: 'bold'
  },
  warningText: {
    color: '#f57c00',
    fontSize: '0.8rem',
    marginTop: '5px',
  },
  updateExistingBox: {
    backgroundColor: '#fff3e0',
    padding: '8px',
    borderRadius: '4px',
    marginTop: '8px',
  },
}));

const FormatInvoice = ({ authInfo, currentRouteSet }) => {
  const classes = useStyles();
  const [formats, setFormats] = useState([]);
  const [updateExisting, setUpdateExisting] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, format: null });
  const [loadingFormats, setLoadingFormats] = useState({}); // Track loading per format

  useEffect(() => {
    currentRouteSet("format_invoice");
    fetchFormats();
  }, []);

  const fetchFormats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/get-invoice-formats`, {
        headers: { 'auth-token': authInfo.token }
      });
      if (!res.data.error) {
        setFormats(res.data.message);
        // Initialize updateExisting and loading state for each format
        const initialUpdateState = {};
        const initialLoadingState = {};
        res.data.message.forEach(f => {
          initialUpdateState[f.format_id] = false;
          initialLoadingState[f.format_id] = false;
        });
        setUpdateExisting(initialUpdateState);
        setLoadingFormats(initialLoadingState);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (index, field, value) => {
    const newFormats = [...formats];
    newFormats[index][field] = value;
    setFormats(newFormats);
  };

  const handleUpdateExistingChange = (formatId, value) => {
    setUpdateExisting(prev => ({ ...prev, [formatId]: value }));
  };

  const handleUpdateClick = (format) => {
    if (updateExisting[format.format_id]) {
      // Show confirmation dialog if updating existing invoices
      setConfirmDialog({ open: true, format });
    } else {
      // Direct update without confirmation
      performUpdate(format, false);
    }
  };

  const handleConfirmUpdate = () => {
    const format = confirmDialog.format;
    setConfirmDialog({ open: false, format: null });
    performUpdate(format, true);
  };

  const performUpdate = async (format, shouldUpdateExisting) => {
    const formatId = format.format_id;
    setLoadingFormats(prev => ({ ...prev, [formatId]: true }));

    try {
      const payload = {
        ...format,
        updateExisting: shouldUpdateExisting
      };

      const res = await axios.post(`${API_URL}/api/update-invoice-format`, payload, {
        headers: { 'auth-token': authInfo.token }
      });

      if (res.data.error) {
        swal({ title: res.data.message, icon: 'warning' });
      } else {
        if (res.data.warning) {
          swal({ title: res.data.message, icon: 'warning' });
        } else {
          swal({ title: res.data.message, icon: 'success' });
        }
        // Reset the updateExisting checkbox after successful update
        if (shouldUpdateExisting) {
          setUpdateExisting(prev => ({ ...prev, [formatId]: false }));
        }
      }
    } catch (err) {
      console.error(err);
      swal({ title: "Error updating format", icon: 'error' });
    } finally {
      setLoadingFormats(prev => ({ ...prev, [formatId]: false }));
    }
  };

  const getPreview = (format) => {
    let invoice = format.prefix || "";
    const separator = format.number_separator !== undefined ? format.number_separator : '-';

    if (Boolean(format.include_year)) {
      if (invoice) invoice += separator;
      invoice += new Date().getFullYear();
    }

    if (invoice) invoice += separator;

    let num = "1";
    if (format.number_digits > 0) {
      num = num.padStart(format.number_digits, '0');
    }
    invoice += num;

    if (format.suffix) invoice += format.suffix;

    return invoice;
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.title}>Invoice Formatting</Typography>
      <Grid container spacing={3}>
        {formats.map((format, index) => (
          <Grid item xs={12} md={6} lg={4} key={format.format_id}>
            <Paper className={classes.paper}>
              <Typography variant="h6" style={{ marginBottom: 10, color: '#109a07' }}>
                {format.type} Invoice
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Prefix"
                    fullWidth
                    value={format.prefix || ''}
                    onChange={(e) => handleChange(index, 'prefix', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Suffix"
                    fullWidth
                    value={format.suffix || ''}
                    onChange={(e) => handleChange(index, 'suffix', e.target.value)}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(format.include_year)}
                        onChange={(e) => handleChange(index, 'include_year', e.target.checked ? 1 : 0)}
                        color="primary"
                      />
                    }
                    label="Include Year"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Separator"
                    fullWidth
                    value={format.number_separator !== undefined ? format.number_separator : '-'}
                    onChange={(e) => handleChange(index, 'number_separator', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Number Digits (Padding)"
                    type="number"
                    fullWidth
                    value={format.number_digits}
                    onChange={(e) => handleChange(index, 'number_digits', parseInt(e.target.value) || 0)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Paper variant="outlined" style={{ padding: 10, backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                    <Typography variant="caption">Preview:</Typography>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                      {getPreview(format)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <div className={classes.updateExistingBox}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={updateExisting[format.format_id] || false}
                          onChange={(e) => handleUpdateExistingChange(format.format_id, e.target.checked)}
                          color="secondary"
                        />
                      }
                      label={<Typography variant="body2"><strong>Update Existing Invoices</strong></Typography>}
                    />
                    {updateExisting[format.format_id] && (
                      <Typography className={classes.warningText}>
                        <WarningIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />
                        All existing {format.type} invoices will be regenerated with the new format!
                      </Typography>
                    )}
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={loadingFormats[format.format_id] ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    fullWidth
                    onClick={() => handleUpdateClick(format)}
                    disabled={loadingFormats[format.format_id]}
                  >
                    {loadingFormats[format.format_id] ? 'Updating...' : 'Update'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, format: null })}
      >
        <DialogTitle style={{ color: '#f57c00' }}>
          <WarningIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Confirm Update Existing Invoices
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to update all existing <strong>{confirmDialog.format?.type}</strong> invoices?
            <br /><br />
            This will regenerate all invoice numbers with the new format. This action cannot be undone.
            <br /><br />
            <strong>Note:</strong> Related records (like returns) will also be updated to maintain data integrity.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, format: null })} color="default">
            Cancel
          </Button>
          <Button onClick={handleConfirmUpdate} color="secondary" variant="contained">
            Yes, Update All
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapState = (state) => ({
  authInfo: state.authInfoReducer,
});

export default connect(mapState, { currentRouteSet })(FormatInvoice);

